import { useState } from "react";
import { toast } from "sonner";
import { Bell } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const LABELS = {
  employer: [
    ["applications", "New applications", "Email me and my team when a candidate applies, with their details."],
    ["messages", "New chat messages", "Email me when a candidate's message goes unread for over an hour."],
    ["jobs", "Listing updates", "Approval, rejection and expiry emails for my listings."],
    ["product", "Tips and check-ins", "Occasional friendly tips and weekly check-ins."],
  ],
  candidate: [
    ["updates", "Application updates", "Confirmations and status changes on my applications."],
    ["messages", "New chat messages", "Email me when an employer's message goes unread for over an hour."],
    ["alerts", "New roles near me", "Email me when a job matching my location goes live."],
    ["product", "Tips and check-ins", "Weekly roles near me and friendly check-ins."],
  ],
  admin: [
    ["product", "Tips and check-ins", "Occasional product emails."],
  ],
};

export default function NotificationPrefs() {
  const { user, refreshUser } = useAuth();
  const [busy, setBusy] = useState("");
  if (!user) return null;
  const prefs = { applications: true, updates: true, jobs: true, product: true, alerts: true, messages: true, ...(user.email_prefs || {}) };
  const items = LABELS[user.role] || LABELS.candidate;

  const toggle = async (key) => {
    setBusy(key);
    try {
      await api.put("/auth/notification-prefs", { prefs: { ...prefs, [key]: !prefs[key] } });
      await refreshUser();
      toast.success("Notification settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="glass rounded-lg p-6 sm:p-8" data-testid="notification-prefs">
      <div className="flex items-center gap-2 mb-1">
        <Bell className="h-4 w-4 text-blue-600" />
        <h2 className="font-display text-lg font-bold text-zinc-950">Email notifications</h2>
      </div>
      <p className="text-sm text-zinc-500 mt-1 mb-4">Choose which emails you receive. Account and security emails always send.</p>
      <div className="space-y-1">
        {items.map(([key, label, hint]) => (
          <button key={key} type="button" role="switch" aria-checked={!!prefs[key]} disabled={busy === key}
            onClick={() => toggle(key)} data-testid={`pref-toggle-${key}`}
            className="group w-full flex items-center justify-between gap-4 rounded-lg px-3 py-2.5 -mx-3 text-left cursor-pointer hover:bg-zinc-50 active:bg-zinc-100 transition-colors duration-200">
            <span>
              <span className="block text-sm font-medium text-zinc-800 group-hover:text-zinc-950 transition-colors duration-200">{label}</span>
              <span className="block text-xs text-zinc-400 mt-0.5">{hint}</span>
            </span>
            <span className={`relative shrink-0 h-6 w-11 rounded-full transition-colors duration-200 ${prefs[key] ? "bg-blue-600 group-hover:bg-blue-700" : "bg-zinc-300 group-hover:bg-zinc-400"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${prefs[key] ? "translate-x-5" : "translate-x-0.5"}`} />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
